<template>
  <div>
    <b-form-checkbox 
      v-model="localSectionCriteria" 
      switch 
      :disabled="exam?.sections.length <= 1 || disabled" 
      class="text-dark font-weight-bold edit-mode mt-5"
    >
      <h5 class="font-weight-bold">เกณฑ์การผ่าน Section</h5>
    </b-form-checkbox>
    
    <template v-if="exam?.sections.length > 1">
      <b-row class="mt-4">
        <b-col class="font-weight-bold" cols="3">
          ชื่อ Section
        </b-col>
        <b-col v-if="localSectionCriteria" class="font-weight-bold" cols="9">
          เปอร์เซ็นต์การผ่าน
        </b-col>
      </b-row>
      
      <template v-if="exam">
        <b-row v-for="(section, index) in exam.sections" :key="section.id" class="mt-4" style="min-height: 38px;">
          <b-col cols="3">
            {{ section.title }}
          </b-col>
          <b-col cols="2" v-if="localSectionCriteria">
            <div class="input-group" style="width: 120px;">
              <b-form-input
                type="text"
                class="form-control passing-percentage-input"
                style="width: 50px;"
                placeholder="-"
                v-model="section.criteria[1].lowerBoundRange"
                :disabled="disabled"
                @blur="!disabled && updateSectionCriteria(index)"
                @keypress="!disabled && onSectionCriteriaPercentKeypress($event, section.criteria[1].lowerBoundRange)"
              />
              <div class="input-group-prepend">
                <span class="input-group-text" style="border-top-right-radius: 0.25rem; border-bottom-right-radius: 0.25rem;">
                  %
                </span>
              </div>
            </div>
          </b-col>
        </b-row>
      </template>
    </template>
    
    <template v-else class="mb-10">
      <p class="have-one-section-warning">ไม่สามารถตั้งค่าเกณฑ์การผ่าน Section ได้ เนื่องจากข้อสอบมีเพียง 1 section</p>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  name: "SectionCriteria",
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      token: localStorage.getItem("token"),
      localSectionCriteria: false,
      isInitialMount: true,
    };
  },
  async mounted() {
    this.localSectionCriteria = this.value ?? false;
    this.$nextTick(() => {
      this.isInitialMount = false;
    });
  },
  computed: {
    ...mapGetters("ExamBuilder", ["exam"]),
  },
  methods: {
    ...mapActions("ExamBuilder", [
      "updateOptions",
      "updateExamSectionCriteria"
    ]),
    _clampSectionCriteria(section) {
      const v = Number(section.criteria[1].lowerBoundRange) || 1;
      section.criteria[1].lowerBoundRange = Math.min(100, Math.max(1, v)).toFixed(2);
    },
    _onSectionCriteriaPercentKeypress(event, currentValue) {
      if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(event.key)) {
        return;
      }

      if (!/[0-9.]/.test(event.key)) {
        event.preventDefault();
        return;
      }

      let strValue = (currentValue ?? '').toString();
      if (event.key === '.' && strValue && strValue.includes('.')) {
        event.preventDefault();
        return;
      }

      // Limit to 3 digits before decimal point
      const parts = strValue.includes('.') ? strValue.split('.') : ['', ''];
      if (parts[0] && parts[0].length >= 3 && event.key !== '.' && !strValue.includes('.')) {
        event.preventDefault();
        return;
      }

      // Limit to 2 decimal places
      if (parts[1] && parts[1].length >= 2) {
        event.preventDefault();
        return;
      }
    },
    _generateSectionCriteria(lowerBound) {
      const subtraction = 0.01; // gap between F and P grades
      
      return [
        {
          grade: "F",
          index: 1,
          lowerBoundRange: 0,
          upperBoundRange: Number((lowerBound - subtraction).toFixed(2))
        },
        {
          grade: "P",
          index: 2,
          lowerBoundRange: Number(lowerBound),
          upperBoundRange: 100
        },
      ];
    },
    // Component methods
    clampSectionCriteria(index) {
      this._clampSectionCriteria(this.exam.sections[index]);
    },
    updateSectionCriteria(index) {
      this.clampSectionCriteria(index);

      const lowerBound = this.exam.sections[index].criteria[1].lowerBoundRange;
      const criteria = this._generateSectionCriteria(lowerBound);
      
      this.$set(this.exam.sections[index], 'criteria', criteria)

      let sectionRequest = {
        body: { sections: this.exam.sections },
        id: this.exam.id,
        token: this.token,
      };

      this.updateExamSectionCriteria(sectionRequest);
    },
    onSectionCriteriaPercentKeypress(e, currentValue) {
      this._onSectionCriteriaPercentKeypress(e, currentValue);
    }
  },
  watch: {
    'exam._options.sectionCriteria'(newVal) {
      if (newVal !== this.localSectionCriteria) {
        this.localSectionCriteria = newVal;
      }
    },
    
    localSectionCriteria(newVal) {
      if (this.isInitialMount || this.disabled) {
        return;
      }

      this.exam._options.sectionCriteria = newVal;
      this.$emit('input', newVal);

      this.updateOptions({
        body: this.exam._options,
        token: this.token,
        id: this.exam.id,
      });

      this.exam.sections.forEach((section) => {
        if (newVal) {
          this.$set(section, 'criteria', [
            { grade: "F", index: 1, lowerBoundRange: 0, upperBoundRange: 59 },
            { grade: "P", index: 2, lowerBoundRange: 60, upperBoundRange: 100 },
          ]);
        } else {
          this.$delete(section, 'criteria');
        }
      });

      this.updateExamSectionCriteria({
        body: { sections: this.exam.sections },
        id: this.exam.id,
        token: this.token,
      });
    },
    value(newVal) {
      this.localSectionCriteria = newVal;
    }
  }
};
</script>

<style scoped>
.passing-percentage-input {
  background-color: #fff !important;
}

.have-one-section-warning {
  color: orange;
  width: 100%;
  padding: 0.75rem 1.25rem;
  background-color: #f9e7d5;
  border: #f9e7d5 1px solid;
  border-radius: 0.25rem;
}
</style>
